import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose: the element owns a live `value` property, which a declared prop
// would shadow with an accessor onto the prop store. The attribute seeds the selection and names
// what a form reset restores.
export const props = Object.freeze({
  /**
   * name attribute used for the element's form value — required to participate in a native form submit.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Group label rendered above the radios and referenced as the radiogroup's accessible name.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Lay the radios out in a row instead of stacked.
   *
   * @type {Boolean|String}
   * @default false
   */
  inline: booleanAttribute(),
  /**
   * Disable the whole group — blocks selection and removes all radios from the tab order.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require one of the radios to be chosen before the form can be submitted. Presence-based, the
   * way the platform reads a boolean attribute: any value other than `false` constrains the group.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required group is submitted with nothing chosen. Names what
   * this particular group is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the current selection visible but block changes.
   *
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * Mark the group as invalid (sets aria-invalid and the error styling).
   *
   * @type {Boolean|String}
   * @default false
   */
  error: booleanAttribute(),
  /**
   * Error text rendered below the radios. Accepts a string or an array of strings.
   *
   * @type {String|Array}
   * @default undefined
   */
  errorMessages: {
    type: [String, Array],
    default: undefined,
  },
});
